import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Select, MenuItem, Button, Box } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { unique } from '../../../utils/collections';
import {
  tableSelectionOptions,
  defaultTableOptions,
  defaultTableInitialState,
} from '../../util/data-table';
import getBuildingStaging from '../../../redux/selectors/staging';
import LocationDetails from '../panels/locations/LocationDetails';
import { deleteLocationsConfirm } from '../dialogs/confirmations';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import { createLocation, deleteLocations, modifyLocations } from '../../../redux/actions/buildings';
import { locationColumns } from '../columns';
import CreateEntity from '../dialogs/CreateEntity';

const Locations = ({
  building,

  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,

  stagingEquipment,
  locationsByName,
  canWrite,

  locationCreate,
  locationsDelete,
  locationModify,
}) => {
  const [confirmation, setConfirmation] = useState({});

  const publishedNames = useMemo(
    () =>
      Object.keys(locationsByName ?? {}).concat(
        stagingEquipment.filter((e) => Boolean(e.published)).map((e) => e.published.name)
      ),
    [locationsByName, stagingEquipment]
  );

  const columns = useMemo(
    () => locationColumns(publishedNames, locationModify),
    [publishedNames, locationModify]
  );

  const data = useMemo(() => Object.values(locationsByName ?? {}), [locationsByName]);

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const locationSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const locationColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    enableGrouping: true,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: locationColumnFilters,
        sorting: locationSorting,
      }),
    },
    getRowId: (l) => l.name,
    enableRowSelection: () => canWrite,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: locationSorting,
      columnFilters: locationColumnFilters,
      showAlertBanner: true,
      isLoading: !locationsByName,
    },
    enableEditing: canWrite,
    editDisplayMode: 'cell',
    enableExpandAll: false,
    // Only allow one expanded at a time
    muiExpandButtonProps: ({ row, table }) => ({
      onClick: () => table.setExpanded({ [row.id]: !row.getIsExpanded() }),
    }),
    renderDetailPanel: ({ row }) => (
      <LocationDetails building={building} loc={row.original} stagingEquipment={stagingEquipment} />
    ),
    // optionally, use single-click to activate editing mode instead of default double-click
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      onClick: () => {
        table.setEditingCell(cell); // set editing cell
        // optionally, focus the text field
        queueMicrotask(() => {
          const textField = table.refs.editInputRefs.current?.[column.id];
          if (textField) {
            textField.focus();
            textField.select?.();
          }
        });
      },
    }),
    renderCreateRowDialogContent: ({ table }) => (
      <CreateEntity
        usedNames={publishedNames}
        submit={(target) => locationCreate(target.entityName.value, target[1].value)}
        onExit={() => table.setCreatingRow(false)}
      >
        <Select defaultValue="ROOM">
          {['ROOM', 'CORRIDOR', 'STAIRWELL', 'FLOOR'].map((type) => (
            <MenuItem id="ltype" key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </CreateEntity>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          color="success"
          disabled={!canWrite}
          onClick={() => table.setCreatingRow(true)}
          variant="contained"
        >
          Create
        </Button>
        <Button
          color="error"
          disabled={
            !canWrite ||
            table.getSelectedRowModel().flatRows.length <= 0 ||
            table.getSelectedRowModel().flatRows?.some(({ original }) => original.published)
          }
          onClick={() => {
            const locations = table.getSelectedRowModel().flatRows.map(({ original }) => original);
            const locationNames = locations.map(({ name }) => name);
            const relatedLocations = unique(
              locations
                .flatMap((loc) => loc?.containsLocations.concat(loc.containedBy))
                .filter(Boolean)
                .filter((name) => !locationNames.includes(name))
            );
            const relatedEquipment = unique(
              locations
                .flatMap((loc) => loc.containsEquipment.concat(loc.fedByEquipment))
                .filter(Boolean)
            );

            return setConfirmation({
              ...deleteLocationsConfirm(
                locationsDelete,
                locationNames,
                relatedLocations.length,
                relatedEquipment.length
              ),
              onExit: () => setConfirmation({}),
            });
          }}
          variant="contained"
        >
          Delete
        </Button>
      </Box>
    ),
  });

  return (
    <>
      <ConfirmDialog
        id={confirmation.id}
        content={confirmation.content}
        onSuccess={confirmation.onSuccess}
        onExit={confirmation.onExit}
      />
      <MaterialReactTable table={mrt} />
    </>
  );
};

const mapStateToProps = (state, { building }) => {
  const {
    entities: {
      staging: { equipmentLoadTime },
      equipTypes,
    },
  } = state;
  const { stagingPoints, stagingEquipment } = getBuildingStaging(state, building.id);
  return {
    equipmentLoadTime: equipmentLoadTime[building.id] ?? false,
    stagingPoints: [...stagingPoints.values()],
    stagingEquipment: [...stagingEquipment.values()],
    equipmentTypes: equipTypes.byId,
    canWrite: building.canWrite,
    locationsByName: state.entities.buildings.locationsByBuildingIdName[building.id],
  };
};

const mapDispatchToProps = (dispatch, { building }) => ({
  locationCreate: (name, type) => dispatch(createLocation(building.id, name, type)),
  locationsDelete: (names) => dispatch(deleteLocations(building.id, names)),
  locationModify: (oldName, newName) =>
    dispatch(modifyLocations(building.id, [{ name: oldName, new_name: newName }])),
});

export default connect(mapStateToProps, mapDispatchToProps)(Locations);
